import DownloadIcon from '@mui/icons-material/Download';
import { Box, Button, ButtonGroup, Paper, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, tooltipClasses } from "@mui/material";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import CustomDialog from '../../components/CustomDialog';
import SearchField from '../../components/SearchField';
import { useApi } from '../../hooks/useApi';
import { useLanguage } from '../../hooks/useLanguage';
import { API_URL } from "../../utils/lib";
import ProductConfiguration from './ProductConfiguration';

/**
 * Displays all lines from the selected product
 * @param {*} param0 
 * @returns 
 */
export default function ProductDetails({ item }) {

  // data-states
  const [data, setData] = useState({});
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);

  // the search-query
  const [query, setQuery] = useState("");
  // ids of all selected product-lines
  const [selected, setSelected] = useState(null);

  // state for opening the selected product-line in a dialog
  const [openSelectLine, setOpenSelectLine] = useState(null);

  const navigate = useNavigate();

  const { locale } = useLanguage();

  const intl = useIntl();
  const api = useApi();

  /**
   * Fetches all product-lines.
   */
  useEffect(() => {
    if (!item || !item.path) return;
    api
      .get(`/3dfindit/details?language=${getLanguage()}&path=${item.path}`)
      .then(response => {
        setData(response.data);
        setColumns(response.data.columns);
        setRows(response.data.lines);
      })
      .catch(error => {
        console.log(error);
      });
  }, [item]);

  const TextTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#f5f5f9',
      color: 'rgba(0, 0, 0, 0.87)',
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(14),
      border: '1px solid #dadde9',
    },
  }));

  /**
   * Get the current language from the locale.
   * @returns 
   */
  const getLanguage = () => {
    switch (locale) {
      case "de":
        return "german";
      default:
        return "english";
    }
  };

  /**
   * Returns all rows of the table.
   * @param {*} rows 
   * @returns 
   */
  const getProductList = (rows) => {
    let selectedRows = [...rows];
    if (selected) {
      selectedRows = selectedRows.filter((_, index) => selected.includes(`${index}`));
    }
    return selectedRows.map((row, index) => {
      let cells = row.cellValues.map((value, index) => {
        value = value.value;
        if (value.length > 50) {
          // if the value is too long, cut it and display a tooltip when hovered
          return <TextTooltip
            key={index + 1}
            title={
              <p>{value}</p>
            }
            arrow
            placement="right"
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, -20],
                    },
                  },
                ],
              },
            }}
          >
            <TableCell align="right" >{value.substring(0, 50)}...</TableCell>
          </TextTooltip>
        }
        return <TableCell key={index + 1} align="right" >{value}</TableCell>
      })
      cells.unshift(<TableCell key={0} sx={{ paddingTop: 0, paddingBottom: 0 }} align="right">
        <Box display="flex" justifyContent="flex-start" alignItems="center">
          <ButtonGroup
            variant="contained"
            aria-label="outlined primary button group"
          >
            <Button
              color="primary"
              aria-label="edit"
              onClick={() => setOpenSelectLine({ tableId: data.tableId, lineId: row.lineId, columns: columns })}
            >
              <DownloadIcon />
            </Button>
          </ButtonGroup>
        </Box>
      </TableCell>)
      return <TableRow key={index} >
        {cells}
      </TableRow>
    });
  };

  /**
   * Searches in the products whenever the query is changed.
   */
  useEffect(() => {
    if (!query) {
      setSelected(null);
      return;
    }
    api
      .get(`/3dfindit/details/search?tableId=${encodeURIComponent(data.tableId)}&search=${query}`)
      .then(response => {
        setSelected(response.data.matchingLines);
      })
      .catch(error => {
        console.log(error);
        setSelected(null);
      });
  }, [query]);

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', height: '200px', marginBottom: '10px' }} >
        <div>
          <img style={{ height: '100%', width: '200px', objectFit: 'contain' }} src={item && item.imagePath ? `${API_URL}/3dfindit/image?imagePath=${item.imagePath}` : null} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', marginLeft: '10px' }}>
          <h1>{item.name}</h1>
          <h2>{item.catalogName}</h2>
        </div>
      </div>
      <SearchField onChange={value => setQuery(value)} />
      <TableContainer component={Paper} sx={{ overflow: 'auto' }} style={{ height: '58vh', marginTop: '10px' }} >
        <Table sx={{ minWidth: 650 }} aria-label="simple table" stickyHeader >
          <TableHead>
            <TableRow>
              <TableCell><FormattedMessage id="Overview.actions" /></TableCell>
              {columns.map((c, index) => {
                return <TableCell key={index} align="right" >{c.description ? c.description : c.name}<br /><p style={{ color: 'grey', display: 'inline' }}>{c.unit ? "(" + c.unit + ")" : ""}</p></TableCell>
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {getProductList(rows)}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomDialog
        title={intl.formatMessage({ id: "ImportFindit3d.configureProduct" })}
        content={<ProductConfiguration product={{ ...openSelectLine }} />}
        isOpen={openSelectLine !== null}
        handleAccept={() => { }}
        handleClose={() => setOpenSelectLine(null)}
        maxWidth={"xl"}
        hideConfirmation={true}
        displayClose={true} />
    </div>
  )
};